import React, { useState } from 'react';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useAppContext } from '../../contexts/Storage';
import { TransactionItem } from '../../models/TransactionItem';
import { TransactionFetchFilter } from '../../models/TransactionFetchFilter';
import { TransactionsPagination } from '../../api/transactions';
import { Button } from '../ui/Button';
import { Input, Checkbox } from '../ui';
import AutocompleteInput from './AutocompleteInput';
import filterIcon from '../../assets/images/filter.png';
import filterIconDark from '../../assets/images-night/filter.png';
import rightIcon from '../../assets/images/arrow_right_grey.png';
import userAvatarPlaceholder from '../../assets/images/profile_placeholder.png';
import TransactionsHeader from './TransactionsHeader';
import { useCurrency } from '../../utils/globalCurrency';

interface EntityFilterValues {
  action: string;
  object: string;
  indirectObject: string;
}

interface TransactionsListProps {
  transactions: TransactionItem[];
  loading: boolean;
  filter: TransactionFetchFilter;
  onFilterChange: (key: keyof TransactionFetchFilter, value: any) => void;
  onEdit: (transaction: TransactionItem) => void;
  onDelete: (transaction: TransactionItem) => void;
  onTransactionClick: (transaction: TransactionItem) => void;
  isSelectedTransactionVisible: boolean;
  recordName?: string;
  pagination?: TransactionsPagination;
  // Entity filter props
  entityFilterValues: EntityFilterValues;
  onEntityFilterValueChange: (field: 'action' | 'object' | 'indirectObject', value: string) => void;
  onEntityFilterSelect: (field: 'action' | 'object' | 'indirectObject', item: { id: number; name: string }) => void;
  onClearEntityFilter: (field: 'action' | 'object' | 'indirectObject') => void;
  onClearAllFilters: () => void;
  hasActiveFilters: boolean;
  createFetchSuggestions: (category: 'action' | 'object' | 'indirect_object' | 'media') => (query: string) => Promise<any[]>;
  // User filter props (optional for now)
  users?: Array<{ id: number; name: string; imageUrl?: string }>;
  onUserFilterChange?: (userId: number | undefined) => void;
}

const TransactionsList: React.FC<TransactionsListProps> = ({
  transactions,
  loading,
  filter,
  onFilterChange,
  onEdit,
  onDelete,
  onTransactionClick,
  isSelectedTransactionVisible,
  recordName,
  pagination,
  // Entity filter props
  entityFilterValues,
  onEntityFilterValueChange,
  onEntityFilterSelect,
  onClearEntityFilter,
  onClearAllFilters,
  hasActiveFilters,
  createFetchSuggestions,
  // User filter props
  users = [],
  onUserFilterChange,
}) => {
  const [showCreatedByColumn, setShowCreatedByColumn] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const { isDark } = useThemeContext();
    const { selectedRecord } = useAppContext();
  const { format } = useCurrency();

  // Helper function to format transaction currency
  const formatTransactionCurrency = (amount?: number) => {
    if (amount === undefined) return 'N/A';
    return format(amount, 2);
  };

  // Pagination computed values
  const currentPage = pagination?.page ?? filter.page ?? 1;
  const lastPage = pagination?.lastPage ?? 1;
  const total = pagination?.total ?? transactions.length;
  const from = pagination?.from ?? (transactions.length > 0 ? 1 : 0);
  const to = pagination?.to ?? transactions.length;
  const hasNextPage = currentPage < lastPage;
  const hasPrevPage = currentPage > 1;

  const getIcon = (lightIcon: string, darkIcon: string) => {
    return isDark ? darkIcon : lightIcon;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return { date: '', time: '' };
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return { date: 'Invalid Date', time: 'Invalid Time' };
      return {
        date: date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }),
        time: date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        })
      };
    } catch (error) {
      return { date: 'Error', time: 'Error' };
    }
  };

  const formatQuantity = (quantity?: number) => {
    if (quantity === undefined || quantity === null) return '';
    return quantity.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const handleRowClick = (transaction: TransactionItem, e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    onTransactionClick(transaction);
  };

  const getDateForInput = (dateString?: string) => {
    if (!dateString) return '';
    return dateString.split(' ')[0];
  };

  const updateDateFilter = (field: 'start_date' | 'end_date', dateValue: string, timeSuffix: string) => {
    if (dateValue) {
      onFilterChange(field, `${dateValue} ${timeSuffix}`);
    } else {
      onFilterChange(field, undefined);
    }
  };

  return (
    <div data-tour="transactions-list" className="bg-light rounded-lg p-4">
      {/* Header with divider */}
      <div className="mb-4">
        <TransactionsHeader transactions={transactions} recordName={recordName} />
        <hr className="mt-4 border-grey_light" />
      </div>

      {/* Filter toggle button */}
      <div data-tour="transactions-filters" className="mb-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center w-full gap-4">
          <Button
            variant="google"
            className="flex items-center w-full sm:w-auto justify-center"
            onClick={() => setShowFilters(!showFilters)}
          >
            <img
              src={getIcon(filterIcon, filterIconDark)}
              alt="Filter"
              className="object-contain w-4 h-4 mr-2"
            />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {hasActiveFilters && (
              <span className="ml-2 bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                Active
              </span>
            )}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="google"
              className="flex items-center w-full sm:w-auto justify-center text-danger"
              onClick={onClearAllFilters}
            >
              Clear All Filters
            </Button>
          )}
          <div className="flex items-center gap-2 bg-bg_grey_light px-4 py-2 rounded-lg">
            <Checkbox
              id="showCreatedByColumn"
              checked={showCreatedByColumn}
              onChange={setShowCreatedByColumn}
              label="Show Created By column"
              className="bg-bg_grey_light"
            />
          </div>
        </div>

        {/* Advanced filters */}
        {showFilters && (
          <div className="mt-4 p-4 bg-bg_grey_light rounded-lg space-y-4">
            {/* Row 1: Transaction ID and Entity Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Transaction ID */}
              <div>
                <label className="block text-sm font-medium mb-2">Transaction ID</label>
                <Input
                  type="text"
                  attributes={{
                    placeholder: "Search by ID...",
                    value: filter.hashedID || '',
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      onFilterChange('hashedID', e.target.value || undefined),
                  }}
                />
              </div>

              {/* Action Filter */}
              <div>
                <label className="block text-sm font-medium mb-2">Action</label>
                <div className="relative">
                  <AutocompleteInput
                    value={entityFilterValues.action}
                    onChange={(value) => onEntityFilterValueChange('action', value)}
                    onSelect={(item) => onEntityFilterSelect('action', item)}
                    placeholder="Search action..."
                    fetchSuggestions={createFetchSuggestions('action')}
                    debounceDelay={300}
                  />
                  {filter.transaction_action_id && (
                    <button
                      onClick={() => onClearEntityFilter('action')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-grey_dark hover:text-dark text-lg z-20"
                      title="Clear"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {/* Object Filter */}
              <div>
                <label className="block text-sm font-medium mb-2">Object</label>
                <div className="relative">
                  <AutocompleteInput
                    value={entityFilterValues.object}
                    onChange={(value) => onEntityFilterValueChange('object', value)}
                    onSelect={(item) => onEntityFilterSelect('object', item)}
                    placeholder="Search object..."
                    fetchSuggestions={createFetchSuggestions('object')}
                    debounceDelay={300}
                  />
                  {filter.transaction_object_id && (
                    <button
                      onClick={() => onClearEntityFilter('object')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-grey_dark hover:text-dark text-lg z-20"
                      title="Clear"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {/* Indirect Object Filter */}
              <div>
                <label className="block text-sm font-medium mb-2">Indirect Object</label>
                <div className="relative">
                  <AutocompleteInput
                    value={entityFilterValues.indirectObject}
                    onChange={(value) => onEntityFilterValueChange('indirectObject', value)}
                    onSelect={(item) => onEntityFilterSelect('indirectObject', item)}
                    placeholder="Search vendor..."
                    fetchSuggestions={createFetchSuggestions('indirect_object')}
                    debounceDelay={300}
                  />
                  {filter.transaction_indirect_object_id && (
                    <button
                      onClick={() => onClearEntityFilter('indirectObject')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-grey_dark hover:text-dark text-lg z-20"
                      title="Clear"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Row 2: Date and Amount Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Start Date</label>
                <Input
                  type="date"
                  attributes={{
                    value: getDateForInput(filter.start_date),
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      updateDateFilter('start_date', e.target.value, '00:00:00'),
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">End Date</label>
                <Input
                  type="date"
                  attributes={{
                    value: getDateForInput(filter.end_date),
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      updateDateFilter('end_date', e.target.value, '23:59:59'),
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Min Amount</label>
                <Input
                  type="number"
                  attributes={{
                    placeholder: "0.00",
                    value: filter.amount_min ?? '',
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      onFilterChange('amount_min', e.target.value === '' ? undefined : parseFloat(e.target.value)),
                    min: 0,
                    step: 0.01
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Max Amount</label>
                <Input
                  type="number"
                  attributes={{
                    placeholder: "No limit",
                    value: filter.amount_max ?? '',
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                      onFilterChange('amount_max', e.target.value ? parseFloat(e.target.value) : undefined),
                    min: 0,
                    step: 0.01
                  }}
                />
              </div>
            </div>

            {/* Row 3: User Filter */}
            {onUserFilterChange && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Created By</label>
                  <div className="relative">
                    <select
                      className="w-full p-2 bg-light text-dark border border-grey_light rounded-lg focus:outline-none focus:border-secondary"
                      value={filter.created_by_user_id || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        onUserFilterChange(value ? parseInt(value, 10) : undefined);
                      }}
                      disabled={users.length === 0}
                    >
                      <option value="">
                        {users.length === 0 ? 'Loading users...' : 'All Users'}
                      </option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                    {filter.created_by_user_id && (
                      <button
                        onClick={() => onUserFilterChange(undefined)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-grey_dark hover:text-dark text-lg z-20"
                        title="Clear"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Transactions list */}
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-2 text-grey_dark">Loading transactions...</p>
        </div>
      ) : transactions.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-grey_dark">No transactions found</p>
          <p className="text-sm text-grey_dark mt-1">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-grey_light">
                <th className="text-left py-3 px-2 text-grey_dark font-normal">Date</th>
                <th className="text-left py-3 px-2 text-grey_dark font-normal">Action</th>
                <th className="text-left py-3 px-2 text-grey_dark font-normal"><div className='flex gap-2'><span className='hidden lg:block'>Direct </span>Object</div></th>
                <th className={`text-left py-3 px-2 text-grey_dark font-normal ${isSelectedTransactionVisible ? 'hidden' : 'hidden lg:block'}`}><div className='flex gap-2'><span className='hidden lg:block'>Indirect</span>Object</div></th>
                <th className="text-left py-3 px-2 text-grey_dark font-normal">Qty</th>
                <th className="text-left py-3 px-2 text-grey_dark font-normal"><div className='flex gap-2'>Payment <span className='hidden lg:block'>Media</span></div></th>
                <th className={`text-left py-3 px-2 text-grey_dark font-normal ${isSelectedTransactionVisible || !showCreatedByColumn ? 'hidden' : 'hidden lg:table-cell'}`}>Created By</th>
                <th className="text-left py-3 px-2 text-grey_dark font-normal">Amount</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction) => {
                const formattedDate = formatDate(transaction.dateTime);
                return (
                  <tr
                    key={transaction.id}
                    className="border-b border-grey_light hover:bg-bg_grey_light cursor-pointer transition-colors duration-150"
                    onClick={(e) => handleRowClick(transaction, e)}
                  >
                    <td className="py-3 px-2">
                      <div className="text-sm text-dark lg:flex">
                        <div className="block mr-2">{formattedDate.date}</div>
                        <div className="text-xs lg:text-sm text-grey_dark">{formattedDate.time}</div>
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="">{transaction.action?.transaction_entity_name}</div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="">{transaction.direct_object?.transaction_entity_name}</div>
                    </td>
                    <td className={`py-3 px-2 ${isSelectedTransactionVisible ? 'hidden' : 'hidden lg:table-cell'}`}>
                      <div className="text-sm text-grey_dark">{transaction.indirect_object?.transaction_entity_name}</div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="text-grey_dark text-sm">x{formatQuantity(transaction.qty)}</div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="text-primary text-sm">{transaction.paymentMedia?.transaction_entity_name}</div>
                    </td>
                    <td className={`py-3 px-2 ${isSelectedTransactionVisible || !showCreatedByColumn ? 'hidden' : 'hidden lg:table-cell'}`}>
                      <div className="flex items-center gap-2">
                        {(() => {
                          if (!transaction.created_by_user_id) {
                            return (
                              <>
                                <img
                                  className="mx-2 rounded-full object-cover"
                                  src={userAvatarPlaceholder}
                                  alt="User Avatar"
                                  style={{
                                    width: '30px',
                                    height: '30px'
                                  }}
                                />
                                <span className="text-sm text-grey_dark italic">
                                  Not available
                                </span>
                              </>
                            );
                          }

                          if (users.length === 0) {
                            return (
                              <>
                                <img
                                  className="mx-2 rounded-full object-cover"
                                  src={userAvatarPlaceholder}
                                  alt="User Avatar"
                                  style={{
                                    width: '30px',
                                    height: '30px'
                                  }}
                                />
                                <span className="text-sm text-grey_dark italic">
                                  Loading...
                                </span>
                              </>
                            );
                          }

                          const user = users.find(u => u.id === transaction.created_by_user_id);
                          return (
                            <>
                              <img
                                className="mx-2 rounded-full object-cover"
                                src={user?.imageUrl || userAvatarPlaceholder}
                                alt="User Avatar"
                                style={{
                                  width: '30px',
                                  height: '30px'
                                }}
                              />
                              <span className="text-sm text-grey_dark truncate max-w-[100px]">
                                {user?.name || `User ${transaction.created_by_user_id}`}
                              </span>
                            </>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex justify-between items-center">
                                                <span
                          className="transaction-figure"
                          title={formatTransactionCurrency(transaction.amount)}
                        >
                          {formatTransactionCurrency(transaction.amount)}
                        </span>
                        <img
                          src={rightIcon}
                          alt="forward"
                          className={`object-contain w-4 h-4 ${isSelectedTransactionVisible ? 'hidden' : 'hidden lg:block'}`}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {(transactions.length > 0 || total > 0) && (
        <div className="flex justify-between items-center mt-6 pt-4">
          <div className="text-sm text-grey_dark">
            {total === 0
              ? 'No transactions'
              : `Showing ${from} to ${to} of ${total} transactions`}
          </div>
          <div className="flex gap-2">
            <Button
              variant="google"
              onClick={() => onFilterChange('page', Math.max(1, currentPage - 1))}
              disabled={!hasPrevPage}
              className="text-grey_dark"
            >
              Previous
            </Button>
            <Button
              variant="google"
              onClick={() => onFilterChange('page', currentPage + 1)}
              disabled={!hasNextPage}
              className="text-grey_dark"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsList;
